import { Button, Chip, TableCell, Typography } from '@mui/material';
import { DateTime } from 'luxon';
import type { FC } from 'react';
import { Link } from '~/components/atoms/Link';
import { DATEPICKER_FORMAT } from '~/constants';
import { useB2CDocument } from '~/hooks/useB2CDocument';
import type { OrdersTableCellProps } from './OrdersTable.types';

export const OrderTableCell: FC<OrdersTableCellProps> = ({ cellId, row, sx, chipColor, type }) => {
  const { fetchDocument } = useB2CDocument();
  if (row[cellId] === null) {
    return null;
  }

  const cellValue = row[cellId] ?? '';

  /**
   * Handles the document path for the document api
   * @param documentPath - the path to the document api to fetch the aws url
   */
  const documentPathHandler = (documentPath?: string) => {
    if (!documentPath) {
      return;
    }

    // get fetch to documentPath, return the aws url, and open in new tab
    fetchDocument(documentPath).then((url) => {
      window.open(url, '_blank')?.focus();
    });
  };

  return typeof cellValue === 'object' ? (
    <TableCell key={cellId} sx={sx}>
      <Typography component="span" variant="caption" fontWeight={400}>
        {type === 'b2c' ? (
          <Button
            id="b2c_table_cell_document_link_button"
            variant="text"
            onClick={(e) => {
              e.stopPropagation(); // Prevents the row from being selected
              documentPathHandler(cellValue.documentPath);
            }}
            // TODO: Clean up these renderAsLink styles - copied from hcps Button component
            sx={({ palette }) => ({
              fontWeight: 600,
              textDecoration: 'none',
              color: palette.text.primary,
              whiteSpace: 'nowrap',
              ':hover': {
                textDecoration: 'underline',
                cursor: 'pointer',
                color: palette.info.main,
                background: 'transparent !important'
              }
            })}
          >
            {cellValue.id}
          </Button>
        ) : cellValue.documentPath ? (
          <Link id="b2b_table_cell_document_link" to={cellValue.documentPath}>
            {cellValue.id}
          </Link>
        ) : (
          cellValue.id
        )}
      </Typography>
    </TableCell>
  ) : cellId === 'orderDate' && typeof cellValue === 'string' ? (
    <TableCell key={cellId} sx={sx}>
      <Typography component="span" variant="caption" fontWeight={400}>
        {DateTime.fromISO(cellValue).toFormat(DATEPICKER_FORMAT)}
      </Typography>
    </TableCell>
  ) : cellId === 'status' ? (
    <TableCell key={cellId} sx={sx}>
      <Chip label={cellValue} color={chipColor} />
    </TableCell>
  ) : (
    <TableCell key={cellId} sx={sx}>
      <Typography component="span" variant="caption" fontWeight={400}>
        {cellValue}
      </Typography>
    </TableCell>
  );
};
